/**
 * UserStrategyDetail — redesigned strategy detail panel (Wave 3b mockup).
 *
 * Layout:
 *   Header  : name + version picker + "Show code" button
 *   Meta    : strategy description
 *   Params  : Name · Type · Default · Range table (monospace, no row hover)
 *   Configs : manual flat list → GS collapsible groups (pair badge + param string)
 *   Used in : portfolios that reference this strategy (hoverable rows)
 *   Versions: clickable timeline list
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { useUserStrategy } from '../../hooks/useUserStrategies.js';
import {
  useStrategyVersionsList,
  useStrategyGroupedConfigs,
  useStrategyUsedInPortfolios,
  useStrategyOptimizations,
} from '../../hooks/useUserStrategies.js';
import { usePaperTradingStore } from '../../stores/paperTradingStore.js';
import { usePortfoliosStore } from '../../stores/portfoliosStore.js';
import { useOptimizationsPageStore } from '../../stores/optimizationsPageStore.js';
import { Spinner } from '../Spinner/Spinner.js';
import { RouteLink } from '../RouteLink/RouteLink.js';
import { optimizationsTabToSlug } from '../../hooks/useUrlSync.js';
import type { StrategyParam } from '../../types.js';
import type { ConfigWithGsLink, DerivedParam, StrategyVersionSummary, StrategyOptimizationSummary } from '../../api/client.js';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Display label for a version snapshot.
 * Uses the semver string from the code header (e.g. "8.3.0") when present,
 * otherwise falls back to the legacy "v{counter}" format.
 */
function versionLabel(v: StrategyVersionSummary): string {
  return v.semver || `v${v.version}`;
}

function formatParamRange(param: StrategyParam): string {
  if (param.options) {
    const opts = param.options;
    if (opts.length === 0) return '—';
    return opts
      .map((o) => (typeof o === 'object' ? o.value : o))
      .join(' · ');
  }
  if (param.min != null && param.max != null) {
    return `${param.min} – ${param.max}`;
  }
  if (param.min != null) return `≥ ${param.min}`;
  if (param.max != null) return `≤ ${param.max}`;
  return '—';
}

function formatConfigParams(cfg: ConfigWithGsLink): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(cfg.params)) {
    parts.push(`${k} ${v}`);
  }
  parts.push(cfg.timeframe);
  return parts.join(' · ');
}

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ============================================================================
// EmptyState
// ============================================================================

function EmptyState() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--sl-tx-faint)',
        fontSize: 14,
        gap: 8,
        fontFamily: 'var(--sl-ui)',
      }}
    >
      <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" opacity={0.4}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <p style={{ margin: 0 }}>Select a strategy to view details</p>
    </div>
  );
}

// ============================================================================
// SectionHeader
// ============================================================================

function SectionHeader({ label, count }: { label: string; count?: number }) {
  return (
    <h3 style={{
      fontSize: 11,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--sl-tx-faint)',
      fontWeight: 600,
      margin: '0 0 11px',
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      fontFamily: 'var(--sl-ui)',
    }}>
      {label}
      {count != null && (
        <span style={{ color: 'var(--sl-tx-ghost)' }}>{count}</span>
      )}
      <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, var(--sl-border), transparent)' }} />
    </h3>
  );
}

// ============================================================================
// VersionPicker
// ============================================================================

interface VersionPickerProps {
  /** Display label shown in the picker button (e.g. "8.3.0" or "v11" fallback). */
  currentVersionLabel: string;
  /** Integer snapshot counter of the active version — used to highlight the active row. */
  activeVersionNum: number;
  versions: StrategyVersionSummary[];
  onSelect: (v: StrategyVersionSummary) => void;
}

function VersionPicker({ currentVersionLabel, activeVersionNum, versions, onSelect }: VersionPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: '1px solid var(--sl-border-2)',
          borderRadius: 8,
          padding: '5px 10px',
          fontSize: 12,
          color: 'var(--sl-tx-dim)',
          fontFamily: 'var(--sl-mono)',
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {currentVersionLabel} ▾
      </button>
      {open && versions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          minWidth: 280,
          background: 'var(--sl-elev)',
          border: '1px solid var(--sl-border-2)',
          borderRadius: 10,
          boxShadow: '0 20px 50px -20px #000',
          zIndex: 12,
          overflow: 'hidden',
        }}>
          {[...versions].reverse().map((v) => {
            const isCur = v.version === activeVersionNum;
            return (
              <div
                key={v.id}
                onClick={() => { onSelect(v); setOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '9px 12px',
                  borderBottom: '1px solid var(--sl-border)',
                  cursor: 'pointer',
                  background: isCur ? 'var(--sl-accent-bg)' : 'transparent',
                }}
                onMouseEnter={(e) => { if (!isCur) (e.currentTarget as HTMLDivElement).style.background = 'var(--sl-surface)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = isCur ? 'var(--sl-accent-bg)' : 'transparent'; }}
              >
                <span style={{ fontFamily: 'var(--sl-mono)', fontWeight: 600, fontSize: 12.5, color: 'var(--sl-tx)', minWidth: 54 }}>
                  {versionLabel(v)}
                </span>
                <span style={{ color: 'var(--sl-tx-dim)', fontSize: 11.5, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.changeSummary ?? v.name}
                </span>
                <span style={{ color: 'var(--sl-tx-faint)', fontSize: 11, fontFamily: 'var(--sl-mono)' }}>
                  {timeAgo(v.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ParamsTable
// ============================================================================

function ParamsTable({ params }: { params: StrategyParam[] }) {
  if (params.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', fontStyle: 'italic', margin: 0 }}>
        No parameters defined.
      </p>
    );
  }

  const colStyle: React.CSSProperties = {
    fontFamily: 'var(--sl-mono)',
    fontSize: 12.5,
    textAlign: 'left',
    color: 'var(--sl-tx)',
  };

  return (
    <div>
      <p style={{ fontSize: 11.5, color: 'var(--sl-tx-faint)', margin: '0 0 8px', fontStyle: 'italic' }}>
        Each row: name · type · default · range
      </p>
      <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          columnGap: 22,
          marginBottom: -1,
        }}>
          {params.map((p) => (
            <div key={p.name} style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              padding: '5px 10px',
              borderBottom: '1px solid var(--sl-border)',
              minWidth: 0,
            }}>
              <span style={{ ...colStyle, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <span style={{ ...colStyle, color: 'var(--sl-tx-faint)', fontSize: 11 }}>{p.type}</span>
              <span style={{ ...colStyle, textAlign: 'right', minWidth: 40 }}>{String(p.default)}</span>
              <span style={{ ...colStyle, color: 'var(--sl-tx-dim)', fontSize: 11, minWidth: 56, textAlign: 'right' }}>{formatParamRange(p)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DerivedParamsTable  (shown when strategy.params is empty)
// ============================================================================

function DerivedParamsTable({ params }: { params: DerivedParam[] }) {
  const colStyle: React.CSSProperties = {
    fontFamily: 'var(--sl-mono)',
    fontSize: 12.5,
    textAlign: 'left',
    color: 'var(--sl-tx)',
  };

  return (
    <div>
      <p style={{ fontSize: 11.5, color: 'var(--sl-tx-faint)', margin: '0 0 8px', fontStyle: 'italic' }}>
        Derived from configs — no declared schema. Each row: name · type · a sample value (params vary per config).
      </p>
      <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          columnGap: 22,
          marginBottom: -1,
        }}>
          {params.map((p) => (
            <div key={p.name} style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              padding: '5px 10px',
              borderBottom: '1px solid var(--sl-border)',
              minWidth: 0,
            }}>
              <span style={{ ...colStyle, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
              <span style={{ ...colStyle, color: 'var(--sl-tx-faint)', fontSize: 11 }}>{p.type}</span>
              <span style={{ ...colStyle, textAlign: 'right', minWidth: 48 }}>
                {p.sampleValue != null ? String(p.sampleValue) : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ConfigRow
// ============================================================================

// SHA-39: a navigable config row is now a real <a href> (RouteLink) so
// cmd/ctrl/middle-click can open the config in a new tab, matching every
// other in-app link. `strategyId` is required to build the href whenever
// `onClick` (navigation) is provided; non-navigable rows (no strategyId /
// no onClick) render a plain div, unchanged.
function ConfigRow({ cfg, strategyId, onClick }: { cfg: ConfigWithGsLink; strategyId?: string; onClick?: () => void }) {
  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '110px 1fr',
    alignItems: 'center',
    gap: 12,
    padding: '9px 14px',
    borderTop: '1px solid var(--sl-border)',
    cursor: onClick ? 'pointer' : 'default',
    transition: '0.12s',
    textDecoration: 'none',
    color: 'inherit',
  };
  const hoverHandlers = onClick
    ? {
        onMouseEnter: (e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = 'var(--sl-surface)'; },
        onMouseLeave: (e: React.MouseEvent) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; },
      }
    : {};

  const content = (
    <>
      <span className="sl-badge sl-badge-sym">{cfg.symbol}</span>
      <span style={{
        fontFamily: 'var(--sl-mono)',
        fontSize: 12,
        color: 'var(--sl-tx-dim)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {formatConfigParams(cfg)}
      </span>
    </>
  );

  if (onClick && strategyId) {
    return (
      <RouteLink
        href={`/strategies/${encodeURIComponent(strategyId)}/configs/${encodeURIComponent(cfg.id)}`}
        onNavigate={onClick}
        style={rowStyle}
        {...hoverHandlers}
      >
        {content}
      </RouteLink>
    );
  }

  return (
    <div style={rowStyle} {...hoverHandlers}>
      {content}
    </div>
  );
}

// ============================================================================
// GsGroup
// ============================================================================

/**
 * Detect whether a gs_run_id is a real UUID (from optimized_params) vs a synthetic key
 * (from migration 063 backfill: 'gs-{strat}-{tf}-{ts}').
 * UUID format: 8-4-4-4-12 hex chars.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isRealOptimizationId(gsRunId: string): boolean {
  return UUID_RE.test(gsRunId);
}

function GsGroup({
  group,
  strategyId,
  onClickConfig,
  onNavigateToOptimization,
}: {
  group: { id: string; ranAt: number; configs: ConfigWithGsLink[] };
  strategyId?: string;
  onClickConfig?: (cfg: ConfigWithGsLink) => void;
  onNavigateToOptimization?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const date = new Date(group.ranAt);
  const dateStr = `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;

  // A real optimization run has a UUID gs_run_id; synthetic keys start with 'gs-'.
  const isReal = isRealOptimizationId(group.id);
  const groupLabel = isReal ? 'Grid Search' : 'Config batch';

  return (
    <div style={{
      border: '1px solid var(--sl-border)',
      borderRadius: 10,
      marginBottom: 10,
      overflow: 'hidden',
      background: 'var(--sl-elev)',
    }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          padding: '11px 14px',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--sl-surface)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      >
        <span style={{
          color: 'var(--sl-tx-faint)',
          fontSize: 11,
          transform: open ? 'rotate(90deg)' : 'none',
          transition: '0.18s',
          display: 'inline-block',
        }}>▶</span>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--sl-tx)' }}>
          {groupLabel}{' '}
          <span className="sl-badge sl-badge-ver">{dateStr}</span>
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--sl-tx-faint)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {isReal && onNavigateToOptimization && (
            // SHA-39: a real <a href> — the group header <div onClick> toggles
            // open/close (not a real anchor), so this button is NOT nested
            // inside another anchor; stopPropagation on the wrapping span is
            // sufficient here (no native-anchor-navigation concern like
            // SHA-43's card buttons).
            <span onClick={(e) => e.stopPropagation()}>
              <RouteLink
                href={`/optimizations/${optimizationsTabToSlug('grid_search')}/${encodeURIComponent(group.id)}`}
                onNavigate={() => onNavigateToOptimization(group.id)}
                style={{
                  fontSize: 11,
                  color: 'var(--sl-accent)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                }}
                title="View optimization result"
              >
                View result
              </RouteLink>
            </span>
          )}
          {group.configs.length} configs
        </span>
      </div>
      {open && (
        <div style={{ borderTop: '1px solid var(--sl-border)' }}>
          {group.configs.map((cfg) => (
            <ConfigRow
              key={cfg.id}
              cfg={cfg}
              strategyId={strategyId}
              onClick={onClickConfig ? () => onClickConfig(cfg) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OptimizationsSection  (migration 065 — bidirectional owner link)
// ============================================================================

function fmtShort(n: number): string {
  return n.toFixed(2);
}

function fmtDateShort(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

interface OptimizationsSectionProps {
  strategyId: string;
  onNavigateToOptimization: (id: string, type: 'grid_search' | 'walk_forward') => void;
}

function OptimizationsSection({ strategyId, onNavigateToOptimization }: OptimizationsSectionProps) {
  const { data: optimizations, isLoading } = useStrategyOptimizations(strategyId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
        <Spinner size="sm" className="text-gray-600" />
        Loading...
      </div>
    );
  }

  if (!optimizations || optimizations.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No optimizations yet.
      </p>
    );
  }

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {optimizations.map((opt: StrategyOptimizationSummary) => (
        <RouteLink
          key={opt.id}
          href={`/optimizations/${optimizationsTabToSlug(opt.type)}/${encodeURIComponent(opt.id)}`}
          onNavigate={() => onNavigateToOptimization(opt.id, opt.type)}
          style={{
            display: 'grid',
            gridTemplateColumns: '70px 90px 50px 1fr auto',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderBottom: '1px solid var(--sl-border)',
            cursor: 'pointer',
            transition: '0.12s',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span className={opt.type === 'grid_search' ? 'sl-badge sl-badge-topn' : 'sl-badge sl-badge-weighted'}>
            {opt.type === 'grid_search' ? 'GS' : 'WF'}
          </span>
          <span className="sl-badge sl-badge-sym">{opt.symbol.replace('/USDT:USDT', '').replace('/USDT', '')}</span>
          <span style={{ fontFamily: 'var(--sl-mono)', fontSize: 12, color: 'var(--sl-tx-dim)' }}>
            {opt.timeframe}
          </span>
          <span style={{
            fontFamily: 'var(--sl-mono)',
            fontSize: 12,
            color: opt.sharpeRatio >= 1 ? 'var(--sl-pos)' : opt.sharpeRatio >= 0 ? 'var(--sl-tx-dim)' : 'var(--sl-neg)',
            fontWeight: 600,
          }}>
            {fmtShort(opt.sharpeRatio)}
          </span>
          <span style={{ fontSize: 11, color: 'var(--sl-tx-faint)', fontFamily: 'var(--sl-mono)' }}>
            {fmtDateShort(opt.optimizedAt)}
          </span>
        </RouteLink>
      ))}
    </div>
  );
}

// ============================================================================
// UsedInPortfoliosSection
// ============================================================================

interface UsedInPortfoliosSectionProps {
  strategyId: string;
  /** Integer snapshot counter for the currently-viewed strategy version. */
  activeVersionNum: number;
  /** Human-readable label for the active version (e.g. "8.3.0" or "v11"). */
  activeVersionLabel: string;
  onNavigateToPortfolio: (aggregationId: string) => void;
}

function allocationBadgeClass(mode: string): string {
  if (mode === 'top_n') return 'sl-badge sl-badge-topn';
  if (mode === 'weighted_multi') return 'sl-badge sl-badge-weighted';
  return 'sl-badge sl-badge-single';
}

function UsedInPortfoliosSection({
  strategyId,
  activeVersionNum,
  activeVersionLabel,
  onNavigateToPortfolio,
}: UsedInPortfoliosSectionProps) {
  const { data: portfolios, isLoading } = useStrategyUsedInPortfolios(strategyId, activeVersionNum);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
        <Spinner size="sm" className="text-gray-600" />
        Loading...
      </div>
    );
  }

  if (!portfolios || portfolios.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No portfolios use {activeVersionLabel} of this strategy.
      </p>
    );
  }

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {portfolios.map((p) => (
        <RouteLink
          key={p.aggregationId}
          href={`/portfolios/${encodeURIComponent(p.aggregationId)}`}
          onNavigate={() => onNavigateToPortfolio(p.aggregationId)}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            alignItems: 'center',
            gap: 12,
            padding: '11px 14px',
            borderBottom: '1px solid var(--sl-border)',
            cursor: 'pointer',
            transition: '0.12s',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--sl-tx)' }}>
            {p.name}{' '}
            <span className="sl-badge sl-badge-ver">v{p.currentVersion}</span>
          </span>
          <span className={allocationBadgeClass(p.allocationMode)}>
            {p.allocationMode !== 'weighted_multi' && p.allocationMode !== 'top_n' ? null : (
              <span className="sl-dot" />
            )}
            {p.allocationMode.replace('_', ' ')}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--sl-tx-dim)' }}>
            uses {p.symbolsUsingThisStrategy.join(', ').replace(/\/USDT/g, '')}
          </span>
        </RouteLink>
      ))}
    </div>
  );
}

// ============================================================================
// VersionsTimeline
// ============================================================================

interface VersionsTimelineProps {
  versions: StrategyVersionSummary[];
  /** Integer snapshot counter of the active version — used to highlight the active row. */
  activeVersionNum: number;
  onSelect: (v: StrategyVersionSummary) => void;
}

function VersionsTimeline({ versions, activeVersionNum, onSelect }: VersionsTimelineProps) {
  if (versions.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No version history yet.
      </p>
    );
  }

  const reversed = [...versions].reverse();

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {reversed.map((v) => {
        const isActive = v.version === activeVersionNum;
        return (
          <div
            key={v.id}
            onClick={() => onSelect(v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              padding: '10px 13px',
              borderBottom: '1px solid var(--sl-border)',
              cursor: 'pointer',
              background: isActive ? 'var(--sl-accent-bg)' : 'transparent',
              transition: '0.12s',
            }}
            onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'var(--sl-surface)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = isActive ? 'var(--sl-accent-bg)' : 'transparent'; }}
          >
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              background: isActive ? 'var(--sl-accent)' : 'var(--sl-surface-3)',
              border: `2px solid ${isActive ? 'var(--sl-accent)' : 'var(--sl-border-2)'}`,
              boxShadow: isActive ? '0 0 8px var(--sl-accent-line)' : 'none',
            }} />
            <span style={{ fontFamily: 'var(--sl-mono)', fontWeight: 600, fontSize: 12.5, minWidth: 54, color: 'var(--sl-tx)' }}>
              {versionLabel(v)}
            </span>
            <span style={{ color: 'var(--sl-tx-dim)', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {v.changeSummary ?? v.name}
            </span>
            <span style={{ color: 'var(--sl-tx-faint)', fontSize: 11, fontFamily: 'var(--sl-mono)' }}>
              {timeAgo(v.createdAt)}
            </span>
            <span style={{ color: 'var(--sl-tx-ghost)' }}>›</span>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// StrategyDetailContent
// ============================================================================

interface StrategyDetailContentProps {
  strategyId: string;
}

function StrategyDetailContent({ strategyId }: StrategyDetailContentProps) {
  const { setCodeViewOpen, setCodeViewVersionCode, setSelectedConfigId } = useConfigurationStore();
  const { setActivePage } = usePaperTradingStore();
  const { setSelectedPortfolioId } = usePortfoliosStore();
  const setSelectedOptimizationId = useOptimizationsPageStore((s) => s.setSelectedOptimizationId);
  const setActiveOptimizationsTab = useOptimizationsPageStore((s) => s.setActiveTab);

  // ── version selection state ───────────────────────────────────────────────
  // null = show the current (latest) version; a StrategyVersionSummary = a
  // historical version the user has navigated to.
  const [selectedVersion, setSelectedVersion] = useState<StrategyVersionSummary | null>(null);

  const { data: strategy, isLoading: strategyLoading, error: strategyError } = useUserStrategy(strategyId);
  const { data: versions = [] } = useStrategyVersionsList(strategyId);
  const { data: groupedConfigs, isLoading: configsLoading } = useStrategyGroupedConfigs(strategyId);

  const handleNavigateToPortfolio = (aggregationId: string) => {
    setSelectedPortfolioId(aggregationId);
    setActivePage('portfolios');
  };

  const handleNavigateToOptimization = (id: string, type: 'grid_search' | 'walk_forward') => {
    setActiveOptimizationsTab(type);
    setSelectedOptimizationId(id);
    setActivePage('optimizations');
  };

  // Reset selected version when the strategy changes (e.g. sidebar click)
  useEffect(() => {
    setSelectedVersion(null);
  }, [strategyId]);

  // Ensure the current version always appears in the dropdown / timeline.
  // MUST be declared BEFORE any early return (Rules of Hooks); guards for the
  // strategy being undefined during load.
  const versionsWithCurrent: StrategyVersionSummary[] = useMemo(() => {
    if (!strategy) return versions;
    const eff = strategy.currentVersion ?? 1;
    if (versions.some((v) => v.version === eff)) return versions;
    // strategy.version holds the semver string (e.g. "8.3.0") for the current version.
    const synthetic: StrategyVersionSummary = {
      id: `${strategy.id}-current-synthetic`,
      strategyId: strategy.id,
      version: eff,
      semver: strategy.version || null,
      name: strategy.name,
      code: strategy.code,
      paramsSchema: strategy.params.length > 0 ? strategy.params : null,
      changeSummary: null,
      createdAt: strategy.updatedAt,
    };
    return [...versions, synthetic];
  }, [versions, strategy]);

  if (strategyLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="lg" className="text-gray-400" />
      </div>
    );
  }

  if (strategyError) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-neg)', fontSize: 14 }}>
        Failed to load strategy: {strategyError.message}
      </div>
    );
  }

  if (!strategy) return null;

  // strategy.currentVersion is the integer snapshot counter
  // (user_strategies.current_version, always ≥ 1).
  // strategy.version is the semver string from the strategy code header
  // (e.g. "8.3.0") — a DIFFERENT numbering scheme from the snapshot counter.
  const effectiveCurrentVersion = strategy.currentVersion ?? 1;

  // Integer counter for the active snapshot — used as the comparison key.
  const activeVersionNum = selectedVersion?.version ?? effectiveCurrentVersion;

  // Human-readable label for the picker button:
  // — viewing current version → show strategy.version semver (e.g. "8.3.0") when present,
  //   otherwise fall back to "v{counter}" (handles older strategies with empty version header)
  // — viewing a historical snapshot → show that snapshot's semver or "v{counter}"
  const activeVersionLabel = selectedVersion
    ? (selectedVersion.semver || `v${selectedVersion.version}`)
    : (strategy.version || `v${effectiveCurrentVersion}`);

  const handleSelectVersion = (v: StrategyVersionSummary) => {
    // Clicking the current version deselects (returns to "current" view)
    if (v.version === effectiveCurrentVersion) {
      setSelectedVersion(null);
      setCodeViewVersionCode(null);
    } else {
      setSelectedVersion(v);
    }
  };

  // When a historical version is selected, show only configs pinned to that version.
  // null = current view → show all configs (existing behaviour).
  const filteredByVersion = selectedVersion != null;
  const allManualConfigs = groupedConfigs?.manual ?? [];
  const allGsGroups = groupedConfigs?.gridSearches ?? [];

  const manualConfigs = filteredByVersion
    ? allManualConfigs.filter((c) => c.strategyVersion === selectedVersion.version)
    : allManualConfigs;

  const gsGroups = filteredByVersion
    ? allGsGroups
        .map((g) => ({
          ...g,
          configs: g.configs.filter((c) => c.strategyVersion === selectedVersion.version),
        }))
        .filter((g) => g.configs.length > 0)
    : allGsGroups;

  const totalConfigs = manualConfigs.length + gsGroups.reduce((s, g) => s + g.configs.length, 0);

  return (
    <div style={{ padding: '20px 24px 60px', overflowY: 'auto', flex: 1, fontFamily: 'var(--sl-ui)', color: 'var(--sl-tx)' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, position: 'relative' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', margin: 0, color: 'var(--sl-tx)' }}>
          {strategy.name}
        </h1>
        <VersionPicker
          currentVersionLabel={activeVersionLabel}
          activeVersionNum={activeVersionNum}
          versions={versionsWithCurrent}
          onSelect={handleSelectVersion}
        />
        <span style={{ flex: 1 }} />
        <button
          onClick={() => {
            // When a historical version is selected, pin its code in the viewer.
            // When current, clear any pinned code so the viewer shows the live code.
            setCodeViewVersionCode(selectedVersion ? selectedVersion.code : null);
            setCodeViewOpen(true);
          }}
          style={{
            border: '1px solid var(--sl-border-2)',
            borderRadius: 9,
            padding: '5px 11px',
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--sl-tx)',
            background: 'var(--sl-surface-2)',
            cursor: 'pointer',
          }}
        >
          {selectedVersion ? `Show code (${activeVersionLabel})` : 'Show code'}
        </button>
      </div>

      {/* Meta row — description / version change summary */}
      {selectedVersion ? (
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          color: 'var(--sl-tx-dim)',
          fontSize: 12.5,
          margin: '10px 0 20px',
          flexWrap: 'wrap',
        }}>
          {selectedVersion.changeSummary ? (
            <span>{selectedVersion.changeSummary}</span>
          ) : (
            <span style={{ fontStyle: 'italic', color: 'var(--sl-tx-faint)' }}>No change summary for this version.</span>
          )}
          <span style={{ fontSize: 11, color: 'var(--sl-tx-faint)', fontFamily: 'var(--sl-mono)' }}>
            {new Date(selectedVersion.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        </div>
      ) : strategy.description ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: 'var(--sl-tx-dim)',
          fontSize: 12.5,
          margin: '10px 0 20px',
          flexWrap: 'wrap',
        }}>
          {strategy.description}
        </div>
      ) : (
        <div style={{ marginBottom: 20 }} />
      )}

      {/* Parameters */}
      <div style={{ marginBottom: 24 }}>
        {(() => {
          // When a historical version is selected and has a schema, show that.
          // Fall back to the current declared params or derived params.
          const versionParams: import('../../types.js').StrategyParam[] | null =
            selectedVersion?.paramsSchema ?? null;

          if (versionParams !== null && versionParams.length > 0) {
            return (
              <>
                <SectionHeader label="Parameters" count={versionParams.length} />
                <ParamsTable params={versionParams} />
              </>
            );
          }

          if (versionParams !== null && versionParams.length === 0) {
            // Version snapshot explicitly has an empty schema
            return (
              <>
                <SectionHeader label="Parameters" count={0} />
                <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', fontStyle: 'italic', margin: 0 }}>
                  No parameters defined for this version.
                </p>
              </>
            );
          }

          // Viewing current version (selectedVersion === null) — use strategy.params
          if (strategy.params.length > 0) {
            return (
              <>
                <SectionHeader label="Parameters" count={strategy.params.length} />
                <ParamsTable params={strategy.params} />
              </>
            );
          }

          if (groupedConfigs?.derivedParams && groupedConfigs.derivedParams.length > 0) {
            return (
              <>
                <SectionHeader label="Parameters" count={groupedConfigs.derivedParams.length} />
                <DerivedParamsTable params={groupedConfigs.derivedParams} />
              </>
            );
          }

          return (
            <>
              <SectionHeader label="Parameters" count={0} />
              <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', fontStyle: 'italic', margin: 0 }}>
                No parameters defined.
              </p>
            </>
          );
        })()}
      </div>

      {/* Configurations */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader
          label={filteredByVersion ? `Configurations · on ${activeVersionLabel}` : 'Configurations'}
          count={configsLoading ? undefined : totalConfigs}
        />
        {configsLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
            <Spinner size="sm" className="text-gray-600" />
            Loading configurations...
          </div>
        ) : totalConfigs === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
            {filteredByVersion
              ? `No configurations are pinned to ${activeVersionLabel}.`
              : 'No configurations yet.'}
          </p>
        ) : (
          /* Scroll container — caps height so hundreds of rows don't overflow the page */
          <div style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 2 }}>
            {/* Manual configs */}
            {manualConfigs.length > 0 && (
              <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)', marginBottom: 12 }}>
                {manualConfigs.map((cfg, i) => (
                  <div key={cfg.id} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--sl-border)' }}>
                    <ConfigRow cfg={cfg} strategyId={strategyId} onClick={() => setSelectedConfigId(cfg.id)} />
                  </div>
                ))}
              </div>
            )}
            {/* GS groups */}
            {gsGroups.map((g) => (
              <GsGroup
                key={g.id}
                group={g}
                strategyId={strategyId}
                onClickConfig={(cfg) => setSelectedConfigId(cfg.id)}
                onNavigateToOptimization={(id) => {
                  // Detect GS vs WF — for config-batch groups the type is always GS
                  // (WF does not create strategy_configs in the current model).
                  setActiveOptimizationsTab('grid_search');
                  setSelectedOptimizationId(id);
                  setActivePage('optimizations');
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Optimizations (GS + WF runs owned by this strategy — migration 065) */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Optimizations" />
        <OptimizationsSection
          strategyId={strategyId}
          onNavigateToOptimization={handleNavigateToOptimization}
        />
      </div>

      {/* Used in portfolios — version-filtered to match the active version */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label={`Used in portfolios · on ${activeVersionLabel}`} />
        <UsedInPortfoliosSection
          strategyId={strategyId}
          activeVersionNum={activeVersionNum}
          activeVersionLabel={activeVersionLabel}
          onNavigateToPortfolio={handleNavigateToPortfolio}
        />
      </div>

      {/* Versions */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Versions" />
        <VersionsTimeline
          versions={versionsWithCurrent}
          activeVersionNum={activeVersionNum}
          onSelect={handleSelectVersion}
        />
      </div>
    </div>
  );
}

// ============================================================================
// UserStrategyDetail — exported root component
// ============================================================================

export function UserStrategyDetail() {
  const { selectedStrategyId } = useConfigurationStore();

  if (!selectedStrategyId) {
    return (
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: 'var(--sl-bg)' }}>
      <StrategyDetailContent strategyId={selectedStrategyId} />
    </div>
  );
}
